import { useLazyQuery, useQuery } from "@apollo/client";
import { Card, Drawer, ScrollArea } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  GET_RETAILERS_WITH_SUPPLIERS,
  GET_SUPPLIERS_BY_RETAILER,
} from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import B2bTable from "components/reusable/b2bTable";
import { useEffect, useState } from "react";
import { FiEye } from "react-icons/fi";
import ExportComponent from "utiles/export";
import { formatNumber } from "utiles/url";

const RetailersWithSuppliers = () => {
  const [selectedRetailerId, setSelectedRetailerId] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [size, setSize] = useState("10");
  const [searchValue, setSearchValue] = useState("");
  const [confirmedSearch, setConfirmedSearch] = useState("");
  const [supplierPage, setSupplierPage] = useState(1);
  const [supplierSize, setSupplierSize] = useState("10");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [confirmedSupplierSearch, setConfirmedSupplierSearch] = useState("");

  const {
    data,
    loading,
    refetch: refetchRetailers,
  } = useQuery(GET_RETAILERS_WITH_SUPPLIERS, {
    variables: {
      first: parseInt(size),
      page: activePage,
      search: searchValue,
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Failed to get retailers with suppliers",
      });
    },
  });

  const {
    data: supplierData,
    loading: supplierLoading,
    refetch,
  } = useQuery(GET_SUPPLIERS_BY_RETAILER, {
    variables: {
      retailer_id: selectedRetailerId || "0",
      first: parseInt(supplierSize),
      page: supplierPage,
      search: supplierSearch,
    },
    skip: !selectedRetailerId,
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Failed to get suppliers for retailer",
      });
    },
  });

  const header = [
    { label: "Name", key: "name", render: (row) => <span>{row.name}</span> },
    { label: "Phone", key: "phone", render: (row) => <span>{row.phone}</span> },
    { label: "Email", key: "email", render: (row) => <span>{row.email}</span> },
    {
      label: "Suppliers",
      key: "supplier_count",
      render: (row) => <span>{formatNumber(row.supplier_count)}</span>,
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      render: (row) => (
        <ActionButton
          color="success"
          label="View Suppliers"
          onClick={() => {
            setSelectedRetailerId(row.retailer_id);
            setSupplierPage(1);
            setSupplierSearch("");
            setConfirmedSupplierSearch("");
          }}
        >
          <FiEye fontSize="medium" />
        </ActionButton>
      ),
    },
  ];

  const supplierHeader = [
    { label: "Name", key: "name", render: (row) => <span>{row.name}</span> },
    { label: "Phone", key: "phone", render: (row) => <span>{row.phone}</span> },
    { label: "Email", key: "email", render: (row) => <span>{row.email}</span> },
    {
      label: "Status",
      key: "status",
      render: (row) => <span>{row.status}</span>,
    },
    {
      label: "SME",
      key: "is_sme",
      render: (row) => <span>{row.is_sme ? "Yes" : "No"}</span>,
    },
    {
      label: "Added At",
      key: "added_at",
      render: (row) => <span>{row.added_at || "N/A"}</span>,
    },
  ];

  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
    setActivePage(1);
  };
  const [fetchAllRetailers] = useLazyQuery(GET_SUPPLIERS_BY_RETAILER);

  const handleExportDataFetch = async () => {
    const { data: allData } = await fetchAllRetailers({
      variables: {
        retailer_id: selectedRetailerId,
        first: null,
        page: 1,
        search: supplierSearch,
      },
    });
    return allData?.suppliersByRetailerId?.data || [];
  };
  useEffect(() => {
    refetch();
    refetchRetailers();
  }, []);
  return (
    <>
      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={data?.retailersWithSuppliers?.paginatorInfo?.lastPage || 0}
            paginatorInfo={data?.retailersWithSuppliers?.paginatorInfo}
            activePage={activePage}
            handleChange={setActivePage}
            clearInput={clearInput}
            handelSearch={(value) => {
              setSearchValue(value);
              setActivePage(1);
            }}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            header={header}
            loading={loading}
            data={(data?.retailersWithSuppliers?.data || []).map(
              (retailer) => ({
                ...retailer,
                id: retailer.retailer_id,
              }),
            )}
            size={size}
            handlePageSizeChange={(newSize) => {
              setSize(newSize);
              setActivePage(1);
            }}
          />
        </ScrollArea>
      </Card>
      <Drawer
        opened={Boolean(selectedRetailerId)}
        onClose={() => setSelectedRetailerId(null)}
        title="Suppliers Linked To Retailer"
        padding="xl"
        size="80%"
        position="bottom"
      >
        <ScrollArea>
          <B2bTable
            total={
              supplierData?.suppliersByRetailerId?.paginatorInfo?.lastPage || 0
            }
            paginatorInfo={supplierData?.suppliersByRetailerId?.paginatorInfo}
            activePage={supplierPage}
            handleChange={setSupplierPage}
            clearInput={() => {
              setSupplierSearch("");
              setConfirmedSupplierSearch("");
              setSupplierPage(1);
            }}
            handelSearch={(value) => {
              setSupplierSearch(value);
              setSupplierPage(1);
            }}
            searchValue={confirmedSupplierSearch}
            onSearchChange={setConfirmedSupplierSearch}
            header={supplierHeader}
            loading={supplierLoading}
            data={(supplierData?.suppliersByRetailerId?.data || []).map(
              (supplier) => ({ ...supplier, id: supplier.supplier_id }),
            )}
            filterData={({ onCardClick }) => (
              <ExportComponent
                data={
                  supplierData ? supplierData.suppliersByRetailerId.data : []
                }
                headers={supplierHeader}
                onFetch={handleExportDataFetch}
                reportTitle="Suppliers Report"
                fileNamePrefix="suppliers_report"
              />
            )}
            size={supplierSize}
            handlePageSizeChange={(newSize) => {
              setSupplierSize(newSize);
              setSupplierPage(1);
            }}
          />
        </ScrollArea>
      </Drawer>
    </>
  );
};

export default RetailersWithSuppliers;
